import type { OutgoingMessage } from '@/api/hooks/useSendChatMessage'
import type { LocalAttachment } from '@/api/types/MessagingApi'
import {
	pickDocument,
	pickFromLibrary,
	takePhoto,
} from '@/components/SupportPage/AttachmentPicker'
import { Glyph, type GlyphName } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { Pressable } from '@/ui/core/pressable'
import { Text } from '@/ui/core/typography'
import { cn } from '@/ui/utils'
import { Ionicons } from '@expo/vector-icons'
import { BlurView } from 'expo-blur'
import { Image } from 'expo-image'
import { useState } from 'react'
import { Platform } from 'react-native'
import {
	type LayoutChangeEvent,
	ScrollView,
	StyleSheet,
	TextInput,
	View,
} from 'react-native'
import Animated, {
	interpolateColor,
	useAnimatedStyle,
	useSharedValue,
	withTiming,
} from 'react-native-reanimated'

const MAX_LENGTH = 5000
/** One line of the field's text, its vertical padding, and how many lines it grows before it scrolls. */
const LINE = 20
const INPUT_PAD = 13
const MAX_LINES = 6
/** The field's one-line height, border included; the disc beside it matches. */
const FIELD = INPUT_PAD * 2 + LINE + 2

const isImage = (file: LocalAttachment) => file.contentType.startsWith('image/')

/** A picked file waiting to go: a thumbnail for a photo, a tile for anything else, with a cross. */
const Pending = ({ file, onRemove }: { file: LocalAttachment; onRemove: () => void }) => (
	<View className="relative">
		{isImage(file) ? (
			<Image
				source={{ uri: file.uri }}
				contentFit="cover"
				style={{ width: 64, height: 64, borderRadius: 12 }}
			/>
		) : (
			<View className="h-16 w-24 justify-end rounded-brand bg-white px-2 py-1.5">
				<Ionicons name="document-outline" size={18} color={colors.ink} />
				<Text size="body2" numberOfLines={1}>
					{file.filename}
				</Text>
			</View>
		)}
		<Pressable
			onPress={onRemove}
			accessibilityRole="button"
			accessibilityLabel={`Remove ${file.filename}`}
			haptic={false}
			hitSlop={12}
			className="absolute -top-1.5 -right-1.5 h-6 w-6 items-center justify-center rounded-full bg-ink"
		>
			<Ionicons name="close" size={14} color={colors.white} />
		</Pressable>
	</View>
)

/**
 * A field along the card's foot, shaped like every other input, with a disc to add a file on
 * the left and an ink disc to send on the right that lights once there is something to send.
 * Picked files wait in a row above the field until they go. The input has no padding of its
 * own: the frame centres it, so one line sits on the middle and more lines grow the frame.
 */
export const ChatComposer = ({
	draft,
	onChangeDraft,
	onSend,
	disabled = false,
	onHeight,
	files,
	onChangeFiles,
	onPicked,
}: {
	draft: string
	onChangeDraft: (text: string) => void
	onSend: (message: OutgoingMessage) => void
	disabled?: boolean
	/** Reports the strip's height, so the thread can keep its last bubble clear of it. */
	onHeight?: (height: number) => void
	/** Files waiting to go, and the two ways they change: picked here, or removed from the row. */
	files: LocalAttachment[]
	onChangeFiles: (files: LocalAttachment[]) => void
	onPicked: (picked: LocalAttachment[]) => void
}) => {
	// One value, 0 closed to 1 open, drives the disc's turn, the field's fade and the pill's slide.
	const [fanned, setFanned] = useState(false)
	const fan = useSharedValue(0)
	const discStyle = useAnimatedStyle(() => ({
		transform: [{ rotate: `${fan.value * 45}deg` }],
	}))
	// The frame itself dissolves while open: the blocks stand on the card on their own.
	const frameStyle = useAnimatedStyle(() => ({
		borderColor: interpolateColor(
			fan.value,
			[0, 1],
			[colors.edge, 'rgba(255,255,255,0)']
		),
		backgroundColor: interpolateColor(
			fan.value,
			[0, 1],
			[colors.white, 'rgba(255,255,255,0)']
		),
	}))
	const fieldStyle = useAnimatedStyle(() => ({
		opacity: 1 - fan.value,
		transform: [{ translateX: -fan.value * 12 }],
	}))
	const pillStyle = useAnimatedStyle(() => ({
		opacity: fan.value,
		transform: [{ translateX: (1 - fan.value) * 12 }],
	}))
	const toggleFan = () => {
		const next = !fanned
		setFanned(next)
		fan.value = withTiming(next ? 1 : 0, { duration: 200 })
	}
	const pickThen = (pick: () => Promise<LocalAttachment[]>) => async () => {
		toggleFan()
		const picked = await pick()
		if (picked.length) onPicked(picked)
	}
	const hasCamera = Platform.OS === 'ios' || Platform.OS === 'android'
	// Sends in flight show in the thread, not here: the disc only cares whether there is something to send.
	const canSend = (draft.trim().length > 0 || files.length > 0) && !disabled

	const send = () => {
		if (!canSend) return
		onSend({ body: draft.trim(), attachments: files })
		onChangeDraft('')
		onChangeFiles([])
	}

	return (
		<View
			className="absolute right-0 bottom-0 left-0 px-4 pt-4 pb-4"
			onLayout={(e: LayoutChangeEvent) => onHeight?.(e.nativeEvent.layout.height)}
		>
			{/* Glass: the thread shows through a blur with a wash of the card's blush over it. */}
			<BlurView intensity={28} tint="light" style={StyleSheet.absoluteFill} />
			<View
				style={[
					StyleSheet.absoluteFill,
					{ backgroundColor: colors.blush, opacity: 0.72 },
				]}
			/>
			{files.length > 0 && (
				<ScrollView
					horizontal
					showsHorizontalScrollIndicator={false}
					contentContainerStyle={{
						gap: 10,
						paddingTop: 6,
						paddingBottom: 10,
						paddingRight: 6,
					}}
					keyboardShouldPersistTaps="handled"
				>
					{files.map((file, index) => (
						<Pending
							key={`${file.uri}-${index}`}
							file={file}
							onRemove={() => onChangeFiles(files.filter((_, i) => i !== index))}
						/>
					))}
				</ScrollView>
			)}
			{/* One disc outside on the right: a plus while the field is empty, the send arrow on ink
			    once there is something to send. Files are added before the words. */}
			<View className="flex-row items-end" style={{ gap: 10 }}>
				{/* The padding sits on the frame, not the input, so it stays put while long text
				    scrolls inside the input's six-line cap. */}
				<Animated.View
					className="flex-1 rounded-brand border bg-white"
					style={frameStyle}
				>
					<Animated.View
						className="px-4"
						style={[{ paddingVertical: INPUT_PAD }, fieldStyle]}
						pointerEvents={fanned ? 'none' : 'auto'}
						accessibilityElementsHidden={fanned}
						importantForAccessibility={fanned ? 'no-hide-descendants' : 'auto'}
					>
						<TextInput
							value={draft}
							onChangeText={onChangeDraft}
							placeholder="Send us a love note"
							placeholderTextColor={colors.muted}
							multiline
							scrollEnabled
							maxLength={MAX_LENGTH}
							editable={!disabled}
							className="font-body-medium text-[16px] text-ink"
							style={{
								paddingTop: 0,
								paddingBottom: 0,
								lineHeight: LINE,
								maxHeight: LINE * MAX_LINES,
							}}
							accessibilityLabel="Message"
						/>
					</Animated.View>
					{/* The frame dissolves and three white blocks take its place, full height, a hair apart. */}
					<Animated.View
						pointerEvents={fanned ? 'auto' : 'none'}
						accessibilityElementsHidden={!fanned}
						importantForAccessibility={fanned ? 'auto' : 'no-hide-descendants'}
						className="absolute inset-0 flex-row items-stretch"
						style={[pillStyle, { gap: 6 }]}
					>
						{[
							{
								glyph: 'photo' as GlyphName,
								title: 'Pic',
								spoken: 'Photo library',
								pick: pickFromLibrary,
							},
							...(hasCamera
								? [
										{
											glyph: 'camera' as GlyphName,
											title: 'Cam',
											spoken: 'Camera',
											pick: takePhoto,
										},
								  ]
								: []),
							{
								glyph: 'file' as GlyphName,
								title: 'File',
								spoken: 'File',
								pick: pickDocument,
							},
						].map((o) => (
							<Pressable
								key={o.title}
								onPress={pickThen(o.pick)}
								accessibilityRole="button"
								accessibilityLabel={o.spoken}
								haptic={false}
								className="flex-1 flex-row items-center justify-center rounded-brand border border-edge bg-white"
								style={{ gap: 7 }}
							>
								<Glyph name={o.glyph} size={16} color={colors.ink} />
								<Text size="body1" weight="bold">
									{o.title}
								</Text>
							</Pressable>
						))}
					</Animated.View>
				</Animated.View>
				<Pressable
					onPress={canSend ? send : toggleFan}
					disabled={disabled}
					accessibilityRole="button"
					accessibilityLabel={
						canSend ? 'Send' : fanned ? 'Cancel' : 'Add a photo or file'
					}
					accessibilityState={canSend ? undefined : { expanded: fanned }}
					className={cn(
						'items-center justify-center rounded-full',
						canSend ? 'bg-ink' : 'bg-white'
					)}
					style={{ width: FIELD, height: FIELD }}
				>
					<Animated.View style={canSend ? undefined : discStyle}>
						<Ionicons
							name={canSend ? 'arrow-up' : 'add'}
							size={canSend ? 22 : 26}
							color={canSend ? colors.white : colors.ink}
						/>
					</Animated.View>
				</Pressable>
			</View>
		</View>
	)
}
